import {Inject, Injectable, Injector} from '@angular/core';
import {CountrySelectorService} from '@app/services/country-selector.service';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ConfigService, DcpApiService, FeatureTogglesService} from '@dcp/configurator';
import {ExploreSapConfig} from '@app/services/navigation/model/explore-sap-config.model';
import {BaseSiteService, User} from '@spartacus/core';
import {combineLatest, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {
  EXTERNAL_LINK_DEFAULT_VALUE,
  IS_FULL_DEFAULT_VALUE,
  ShoppingCartConfig
} from '@app/services/navigation/model/shopping-cart-config.model';
import {ContextualSearchConfig} from '@app/services/navigation/model/contextual-search-config.model';
import {ContextualUserMenuConfig} from '@app/services/navigation/model/contextual-user-menu-config.model';
import {ContextualMainLinksConfig} from '@app/services/navigation/model/main-links-config.model';
import {ContextualNavigationLink} from '@app/services/navigation/model/contextual-navigation-common.model';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {DOCUMENT} from '@angular/common';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DcpContextualNavigationService {

  notifications = {
    profile: {
      activityStream: {
        count: 0
      },
      inbox: {
        count: 0
      }
    }
  };

  /**
   * Using one region "World" only as SAP Commerce Cloud does not support global regions like Europe, Americas etc.
   */
  countrySelectorConfig = {
    enabled: true,
    regions: [
      {
        title: 'World',
        countries: []
      }
    ]
  };

  hideExploreSapLink: boolean = true;
  exploreSapLink: ContextualNavigationLink;
  mainLinks: ContextualNavigationLink;
  loggedInUser = undefined;

  landingZone = {
    title: '',
    url: '/'
  };

  initialSearchConfig: ContextualSearchConfig = {
    url: '/dcp/search/',
    placeholder: 'Search the SAP Store',
    inputName: 'query',
    disabled: false
  };

  // override the country selector title according to UX requirements to select the market country not the language
  translations = {
    'CountrySelector.ChooseLanguage': 'Choose your Country'
  };

  homeLogoUrl: string = 'https://sap.com';

  constructor(
    private countrySelectorService: CountrySelectorService,
    private dcpApiService: DcpApiService,
    private userFacade: UserAccountFacade,
    private baseSiteService: BaseSiteService,
    private dcpActiveCartService: DcpActiveCartService,
    private injector: Injector,
    private featureTogglesService: FeatureTogglesService,
    @Inject(DOCUMENT) private document: Document
  ) {
  }

  public loadConfig() {
    this.setLogoUrl();
    this.subscribeToCountries();
    this.subscribeToExploreSap();
    this.subscribeToUserData();
    this.subscribeToMainLinks();
  }

  private subscribeToCountries(): void {
    this.countrySelectorService.getCountries().pipe(untilDestroyed(this)).subscribe(
      (countries) => {
        this.countrySelectorConfig.regions[0].countries = countries.map(country => ({
          displayTitle: country.name,
          countryCode: country.isocode,
          url: 'javascript:void(0)',
        }));
      });
  };

  private subscribeToExploreSap(): void {
    this.dcpApiService.callApi<ExploreSapConfig>(
      'contextualNavigation',
      'exploreSap',
    ).pipe(untilDestroyed(this)).subscribe((exploreSapConfig: ExploreSapConfig) => {
      if (exploreSapConfig.exploreSapLink) {
        this.exploreSapLink = exploreSapConfig.exploreSapLink;
        this.hideExploreSapLink = false;
      }
    });
  }

  private subscribeToMainLinks(): void {
    this.dcpApiService.callApi<ContextualMainLinksConfig>(
      'commerceUiAssets',
      'mainLinks'
    ).pipe(untilDestroyed(this)).subscribe((sapMainLinksConfig: ContextualMainLinksConfig) => {
      this.mainLinks = sapMainLinksConfig.mainLinks as ContextualNavigationLink;
    });
  }

  private subscribeToUserData(): void {
    this.userFacade.get().pipe(untilDestroyed(this)).subscribe((user: User) => {
      if (user) {
        this.countrySelectorConfig.enabled = false;
        this.loggedInUser = {
          name: user.name,
          email: user.email
        };
      } else {
        this.loggedInUser = undefined;
      }
    });
  }

  /**
   * returns ShoppingCartConfig, based on customer and the cart
   * enabled only if customer is logged in,
   * shows notification if the cart is not empty
   */
  getShoppingCartConfig(): Observable<ShoppingCartConfig> {
    return combineLatest([
      this.dcpActiveCartService.getActiveCart(),
      this.baseSiteService.getActive(),
      this.userFacade.get(),
      this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.contextualUserMenuCart)
    ]).pipe(
      untilDestroyed(this),
      map(
        ([cart, baseSite, user, isUserMenuCartEnabled]) => ({
          enabled: !!user && !isUserMenuCartEnabled,
          url: `/${baseSite}/cart`,
          externalLink: EXTERNAL_LINK_DEFAULT_VALUE,
          isFull: IS_FULL_DEFAULT_VALUE,
          hasUnreadNotifications: !!cart?.entries?.length
        })
      )
    );
  }

  /**
   * Returns contextual search configuration
   * TODO: Integrate auto-complete / instant search results
   */
  getContextualSearchConfig(): Observable<ContextualSearchConfig> {
    return this.baseSiteService.getActive()
      .pipe(
        untilDestroyed(this),
        map((activeStore) => (
          {
            url: `/${activeStore}/search/`,
            placeholder: 'Search the SAP Store',
            inputName: 'query',
            disabled: false
          })
        )
      );
  };

  getUserMenuConfig(): Observable<ContextualUserMenuConfig> {
    return combineLatest([
      this.baseSiteService.getActive(),
      this.featureTogglesService.isEnabled(UNLEASH_TOGGLES.contextualUserMenuCart),
      this.dcpApiService.callApi<ContextualUserMenuConfig>(
        'commerceUiAssets',
        'contextualUserMenuConfig'
      )
    ]).pipe(
      untilDestroyed(this),
      map(([baseSite, isUserMenuCartEnabled, userMenuConfig]) => {
        if (isUserMenuCartEnabled) {
          userMenuConfig.links.push({
            title: 'My Shopping Cart',
            url: `${this.document.location.origin}/${baseSite}/cart`
          });
        }
        return userMenuConfig;
      }));
  }

  private setLogoUrl() {
    const url = this.injector.get(ConfigService).getString('logo.url');
    this.homeLogoUrl = url ? url : this.homeLogoUrl;
  }
}
